import { msg } from '@lingui/macro'

import * as React from 'react'

import {
  TFOrderForPaymentMethods,
  TPriceConverted,
  TQAvailablePaymentMethods,
  TQAvailablePaymentMethodsVariables,
  useQAvailablePaymentMethods,
} from '~graphql/generated/clientGraphql'
import { isDefined } from '~logic/helpers/isDefined'

type TOrder = TFOrderForPaymentMethods & {
  totalSum: Pick<TPriceConverted, 'value'>
}

/**
 * Loads branches payment methods.
 *
 * Filters out payment methods that are not in maximum amount limit.
 * Generates messages for those payment methods that are not in limit.
 *
 * @Returns `availableMethods` and `availableMethodsMessages` arrays.
 *
 */
export const useLoadAvailablePaymentMethods = (order: Nullable<TOrder>) => {
  const orderIsNotDefined = !order

  const variables = React.useMemo<TQAvailablePaymentMethodsVariables | undefined>(() => {
    if (orderIsNotDefined) {
      return undefined
    }

    // if handoverType is null, it should return allowed payment methods from config
    return {
      branchId: order.branch.id,
      priceType: order.priceType.enum,
      orderOrigin: order.orderOrigin.enum,
      handoverType: order.handoverType?.enum || null,
    }
  }, [orderIsNotDefined, order])

  const { data } = useQAvailablePaymentMethods({
    variables,
    skip: !variables,
  })

  if (!data) return { availableMethods: null, availableMethodsMessages: null }

  const isOrderInLimit = getIsOrderInLimit(order)
  const getMaxAmountMessage = getMaxAmountMessageFactory(isOrderInLimit)

  const { branchPaymentMethods } = data.branch
  const availableBranchPaymentMethods = branchPaymentMethods.filter(isOrderInLimit)
  const availableMethods = availableBranchPaymentMethods.map(({ method }) => method)
  const availableMethodsMessages = branchPaymentMethods.map(getMaxAmountMessage).filter(isDefined)

  return { availableMethods, availableMethodsMessages }
}

// * HELPERS

type TBranchPaymentMethod = TQAvailablePaymentMethods['branch']['branchPaymentMethods'][number]

// checks if order total sum is lower then payment method max-allowed amount
// curried function - takes `order` as a first argument
// returns a function that takes `method` as an argument and can be used in Array.filter to filter out methods that are not in limit
const getIsOrderInLimit = (order: Nullable<TOrder>) => (method: TBranchPaymentMethod) => {
  const paymentMethodLimit = method.maximumAmount?.value ?? null
  const orderTotalSum = order?.totalSum.value ?? null

  if (!paymentMethodLimit || !orderTotalSum) return true

  return orderTotalSum <= paymentMethodLimit
}

// return a message about the maximum order amount for those payment methods that should be filtered out
// curried function - takes `isInLimit` as a first argument
// returns a function that takes `method` as an argument and can be used in Array.map to generate messages for filtered out methods
const getMaxAmountMessageFactory =
  (isInLimit: ReturnType<typeof getIsOrderInLimit>) => (method: TBranchPaymentMethod) => {
    if (isInLimit(method)) return null
    if (!method.maximumAmount?.value) return null

    const paymentMethodLabel = method.method.label
    const formattedMaximumAmount = method.maximumAmount.formattedValue

    return msg`The maximum order amount for payment type: "${paymentMethodLabel}" is ${formattedMaximumAmount}.`
  }
